import { useEffect, useState } from "react";
import { SITE_CONFIG } from "../site-config";
import type { SiteId, Slot } from "../types";
import ShareActions from "./ShareActions";
import "../styles/success.css";

interface Confirmation {
  paid: boolean;
  ranked: boolean;
  site: SiteId;
  slot?: Slot;
  rank?: number;
}

type ReceiptStatus = "checking" | "processing" | "webhook" | "paid" | "reversed" | "error";

const CONFIRMATION_DELAYS_MS = [0, 1_500, 2_500, 4_000, 6_000, 8_000] as const;

export default function SuccessView({ site }: { site: SiteId }) {
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<ReceiptStatus>("checking");

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId || !/^cs_(?:test_|live_)?[A-Za-z0-9]+$/.test(sessionId)) {
      setError("That checkout receipt has no valid session ID.");
      setStatus("error");
      return;
    }
    const storageKey = `outbid-confirm:${sessionId}`;
    let confirmationToken: string | null = null;
    try {
      confirmationToken = window.sessionStorage.getItem(storageKey);
    } catch {
      // Some privacy modes disable sessionStorage. The signed Stripe webhook
      // still settles the bid without this browser-side receipt capability.
    }
    if (!confirmationToken) {
      setStatus("webhook");
      return;
    }
    const receiptToken = confirmationToken;
    const confirmationUrl = `/api/checkout/confirm?session_id=${encodeURIComponent(sessionId)}`;

    const removeConfirmationToken = () => {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
        // Storage can disappear between reads; there is nothing else to clean up.
      }
    };

    const controller = new AbortController();
    const timeouts = new Set<ReturnType<typeof setTimeout>>();

    const wait = (milliseconds: number) =>
      new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          timeouts.delete(timeout);
          resolve();
        }, milliseconds);
        timeouts.add(timeout);
      });

    async function confirmPayment() {
      let receivedProcessingResponse = false;
      let lastError = "";

      for (const delay of CONFIRMATION_DELAYS_MS) {
        if (delay > 0) await wait(delay);
        if (controller.signal.aborted) return;

        try {
          const response = await fetch(confirmationUrl, {
            cache: "no-store",
            headers: { "X-Outbid-Confirmation": receiptToken },
            signal: controller.signal,
          });
          const body = (await response.json()) as Confirmation & { error?: string };

          if (!response.ok) {
            const message = body.error ?? "Could not confirm this payment.";
            if (response.status < 500 && response.status !== 429) {
              removeConfirmationToken();
              setError(message);
              setStatus("error");
              return;
            }
            lastError = message;
            continue;
          }

          setConfirmation(body);
          if (body.paid) {
            removeConfirmationToken();
            setStatus(body.ranked ? "paid" : "reversed");
            return;
          }

          receivedProcessingResponse = true;
          setStatus("processing");
        } catch (caught) {
          if (controller.signal.aborted) return;
          lastError = caught instanceof Error ? caught.message : "Could not confirm this payment.";
        }
      }

      if (!receivedProcessingResponse) {
        setError(lastError || "Could not confirm this payment.");
        setStatus("error");
      }
    }

    void confirmPayment();

    return () => {
      controller.abort();
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const stamp = status === "paid"
    ? <>PAID<br />&amp; RANKED</>
    : status === "reversed"
      ? <>PAID<br />NOT RANKED</>
    : status === "error"
      ? <>RECEIPT<br />JAMMED</>
      : status === "webhook"
        ? <>PAYMENT<br />PROCESSING</>
        : status === "processing"
          ? <>PAYMENT<br />PROCESSING</>
          : <>CHECKING<br />RECEIPT</>;

  const title = status === "paid"
    ? confirmation?.rank
      ? `You’re #${confirmation.rank}.`
      : "Payment received."
    : status === "reversed"
      ? "This payment no longer counts."
    : status === "error"
      ? "Receipt jam."
      : status === "webhook"
        ? "Your payment is processing."
        : status === "processing"
          ? "Stripe is still thinking."
          : "Counting your bragging rights…";

  const description = status === "paid"
    ? `Your bid is live on ${confirmation?.slot?.label ?? "the board"}. Screenshot this before somebody richer arrives.`
    : status === "reversed"
      ? site === "earth"
        ? "Stripe reports this payment as disputed or otherwise invalidated, so its contribution is not active in the ranking."
        : "Stripe reports this payment as fully refunded or disputed, so its contribution is not active in the ranking."
    : status === "error"
      ? error
      : status === "webhook"
        ? "This tab cannot privately check the receipt, but Stripe’s signed webhook will still process the payment and update the ranking automatically. Return to the board in a moment."
        : status === "processing"
          ? "This checkout is not paid yet. We checked several times and stopped polling; reload this receipt in a moment."
          : "Checking Stripe for a completed payment. No rank is claimed until Stripe confirms it.";

  return (
    <main className={`success-view success-${site}`}>
      <div className="success-stamp">{stamp}</div>
      <p>{SITE_CONFIG[site].domain} certifies that</p>
      <h1>{title}</h1>
      <p>{description}</p>
      {status === "paid" && confirmation?.slot && (
        <section className="success-share" aria-label="Share your rank">
          <strong>Don’t leave quietly.</strong>
          <p>Put your rank in the group chat. The right rival will try to take it.</p>
          <ShareActions
            campaign="winner_receipt"
            content={`${site}_${confirmation.slot.id}_rank_${confirmation.rank ?? "paid"}`}
            countryId={site === "earth" ? confirmation.slot.id : undefined}
            text={`I’m #${confirmation.rank ?? 1} in ${confirmation.slot.label} on ${SITE_CONFIG[site].domain}. Who’s going to outbid me?`}
          />
        </section>
      )}
      <a href="/">Return to the property →</a>
    </main>
  );
}
